import { makeAutoObservable } from 'mobx';
import { groupByFunction } from './utils/rxjs/groupByFunction';
import { v4 as uuidv4 } from 'uuid';
import { updateCurrentLayout } from './utils/outgoingMessages/layouts/currentLayouts/updateCurrentLayout';

export class LayoutStore {
  /*
   *current layout structure*

  currentLayouts = {
    alerts: {
      moduleList: {hash:{}, hash:{}, hash:{}},
      layouts: {lg: [], md: [], sm: []},
    }
  }
  */
  currentLayouts = {
    alerts: {
      moduleList: {
        defaultWatchlist: {
          moduleType: 'alertWatchlist',
          data: { i: 'defaultWatchlist', x: 4, y: 2, w: 4, h: 3, maxW: 8 },
          details: {
            groupNumber: 0,
          },
        },
      },
      layouts: {},
    },
  };
  receivedCurrentLayouts = false;
  savedLayouts = {};
  groupSelectedData = {
    0: {},
    1: {},
    2: {},
    3: {},
    4: {},
    5: {},
    6: {},
  };
  currentFocusedModule = null;
  constructor(root) {
    makeAutoObservable(this);
    this.root = root;
    this.initializeStreams();
  }

  //STREAM INITIALIZATIONS
  initializeStreams = () => {
    const individualSavedLayoutStream = groupByFunction(
      'messageType',
      'savedLayout',
      this.root.websocketSubject
    );
    individualSavedLayoutStream.subscribe(
      this.handleIndividualSavedLayoutMessage
    );

    const allSavedLayoutsStream = groupByFunction(
      'messageType',
      'savedLayouts',
      this.root.websocketSubject
    );
    allSavedLayoutsStream.subscribe(this.handleAllSavedLayoutsMessage);

    const removeSavedLayoutStream = groupByFunction(
      'messageType',
      'removedSavedLayout',
      this.root.websocketSubject
    );
    removeSavedLayoutStream.subscribe(this.handleRemoveSavedLayoutMessage);

    const individualCurrentLayoutStream = groupByFunction(
      'messageType',
      'currentLayout',
      this.root.websocketSubject
    );
    individualCurrentLayoutStream.subscribe(
      this.handleIndividualCurrentLayoutMessage
    );

    const removeCurrentLayoutStream = groupByFunction(
      'messageType',
      'removedCurrentLayout',
      this.root.websocketSubject
    );
    removeCurrentLayoutStream.subscribe(this.handleRemoveCurrentLayoutMessage);

    const allCurrentLayoutsStream = groupByFunction(
      'messageType',
      'currentLayouts',
      this.root.websocketSubject
    );
    allCurrentLayoutsStream.subscribe(this.handleAllCurrentLayoutsMessage);
  };

  //MESSAGE HANDLERS
  handleIndividualSavedLayoutMessage = (msg) => {
    const layout = msg.layout;
    const type = layout.type;
    const hash = `${type}-${layout.name}`;
    if (!this.savedLayouts[type]) {
      this.savedLayouts[type] = {
        hash: layout,
      };
    } else {
      this.savedLayouts[type][hash] = layout;
    }
  };

  handleAllSavedLayoutsMessage = (msg) => {
    const layouts = msg.savedLayouts;
    layouts.forEach((layout) => {
      const type = layout.type;
      const hash = `${type}-${layout.name}`;
      if (!this.savedLayouts[type]) {
        this.savedLayouts[type] = {
          hash: layout,
        };
      } else {
        this.savedLayouts[type][hash] = layout;
      }
    });
  };

  handleRemoveSavedLayoutMessage = (msg) => {
    const layout = msg.layout;
    const type = layout.type;
    const hash = `${type}-${layout.name}`;
    if (this.savedLayouts[type][hash]) delete this.savedLayouts[type][hash];
  };

  handleIndividualCurrentLayoutMessage = (msg) => {
    const layout = msg.layout;
    const type = layout.type;
    this.currentLayouts[type] = layout.json;
  };

  handleRemoveCurrentLayoutMessage = (msg) => {
    this.currentLayouts[msg.layout.type] = {};
  };

  handleAllCurrentLayoutsMessage = (msg) => {
    const layouts = msg.currentLayouts;
    if (layouts.length < 1) {
      this.receivedCurrentLayouts = true;
      return;
    } else
      layouts.forEach((layout) => {
        const type = layout.type;
        this.currentLayouts[type] = layout.json;
      });
    this.receivedCurrentLayouts = true;
  };

  addModuleToCurrentLayout = (
    layoutType,
    moduleType,
    xPos,
    yPos,
    width,
    height,
    details
  ) => {
    const isResizable = true;
    // if (moduleType === 'chart') isResizable = false;
    const hash = uuidv4();
    this.currentLayouts[layoutType].moduleList[hash] = {
      moduleType,
      data: {
        i: hash,
        x: xPos,
        y: yPos,
        w: width,
        h: height,
        maxW: 12,
        minW: moduleType === 'alertWatchlist' ? 3 : 2,
        isResizable,
      },
      details,
    };
  };

  removeModuleFromCurrentLayout = (layoutType, moduleId) => {
    delete this.currentLayouts[layoutType].moduleList[moduleId];
  };

  setCurrentLayouts = (type, newObj) => {
    this.currentLayouts[type].layouts = {
      ...this.currentLayouts[type].layouts,
      ...newObj,
    };
  };
  setSelectedGroupData = (groupNumber, data) => {
    this.groupSelectedData[groupNumber] = data;
  };
  setCurrentFocusedModule(id) {
    this.currentFocusedModule = id;
  }
  updateCurrentLayoutModuleGroupNumber = (type, moduleId, newGroupNumber) => {
    this.currentLayouts[type].moduleList[moduleId].details.groupNumber =
      newGroupNumber;
  };
  updateCurrentLayoutModuleSelectedWatchlistName = (
    type,
    moduleId,
    watchlistName
  ) => {
    this.currentLayouts[type].moduleList[moduleId].details.watchlistName =
      watchlistName;
  };
  updateCurrentLayoutChartModuleData = (type, moduleId, chartDetailsObject) => {
    this.currentLayouts[type].moduleList[moduleId].details.chartData =
      chartDetailsObject;
  };
  updateCurrentLayoutComponentDimensions = (type, moduleId, w, h) => {
    this.currentLayouts[type].moduleList[moduleId].details.width = w;
    this.currentLayouts[type].moduleList[moduleId].details.height = h;
  }
  //debounce this fn call to avoid sending too many update server current layout
  updateServerCurrentLayout = (type, name) => {
    updateCurrentLayout(type, name, this.currentLayouts[type]);
  };

  get alertModuleList() {
    if (!this.currentLayouts.alerts.moduleList) return [];
    return Object.keys(this.currentLayouts.alerts.moduleList);
  }

  get alertWatchlistNamesByGroupNumber() {
    const defaultObj = {
      0: [],
      1: [],
      2: [],
      3: [],
      4: [],
      5: [],
      6: [],
    };
    const obj = {};
    const watchlistModuleArray = Object.keys(
      this.currentLayouts.alerts.moduleList
    ).filter((moduleKey) => {
      return (
        this.currentLayouts.alerts.moduleList[moduleKey].moduleType ===
        'alertWatchlist'
      );
    });
    watchlistModuleArray.forEach((moduleKey) => {
      const watchlistName =
        this.currentLayouts.alerts.moduleList[moduleKey].details
          .watchlistName ?? null;
      const groupNumber =
        this.currentLayouts.alerts.moduleList[moduleKey].details.groupNumber;
      if (watchlistName !== null && watchlistName !== 'default') {
        if (obj[groupNumber])
          obj[groupNumber] = [...obj[groupNumber], watchlistName];
        else obj[groupNumber] = [watchlistName];
      }
    });
    return { ...defaultObj, ...obj };
  }
}
